import React, {useEffect, useMemo, useState} from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import FormSelect from '../../../antd/form/FormSelect/FormSelect.jsx';
import {required} from '../../../antd/form/FormValidation.js';
import Form from '../../../antd/form/Form/Form.jsx';
import FormSwitch from '../../../antd/form/FormSwitch/FormSwitch.jsx';
import CheckboxGroup from 'antd/lib/checkbox/Group';
import FormItem from 'antd/lib/form/FormItem';
import Row from 'antd/lib/grid/row';
import Col from 'antd/lib/grid/col';
import {OVERVIEW_FILTER_ROOM_TYPES} from '../../../../utils/constants/enums.js';
import {useSelector} from "react-redux";
import {Form as AntdForm} from 'antd';
import Tooltip from 'antd/lib/tooltip';

const ReservationsOverviewForm = ({
                                    onFinish,
                                    setShowRooms,
                                     hasDepartmentRooms,
                                  }) => {
  const {t} = useTranslation();
  const user = useSelector(state => state.auth.user);

  const [form] = AntdForm.useForm();
  const [initialDate] = useState(() => dayjs().startOf('month'));


  // Filter options based on authentication
  const filteredOptions = useMemo(() => {
    return user
      ? [
        OVERVIEW_FILTER_ROOM_TYPES.RA,
        OVERVIEW_FILTER_ROOM_TYPES.RB,
        OVERVIEW_FILTER_ROOM_TYPES.RC,
        OVERVIEW_FILTER_ROOM_TYPES.OTHER,
        OVERVIEW_FILTER_ROOM_TYPES.DEPARTMENT_ROOM
      ]
      : [
        OVERVIEW_FILTER_ROOM_TYPES.RA,
        OVERVIEW_FILTER_ROOM_TYPES.RB,
        OVERVIEW_FILTER_ROOM_TYPES.RC,
        OVERVIEW_FILTER_ROOM_TYPES.OTHER
      ];
  }, [user]);

  const defaultCheckedOptions = useMemo(() => {
    if (!user || hasDepartmentRooms) {
      return filteredOptions;
    }

    return filteredOptions.filter(
      (option) => option !== OVERVIEW_FILTER_ROOM_TYPES.DEPARTMENT_ROOM,
    );
  }, [filteredOptions, hasDepartmentRooms, user]);

  const [checkedList, setCheckedList] = useState(defaultCheckedOptions);
  const [filterOn, setFilterOn] = useState(true);
  const [selectedMonthId, setSelectedMonthId] = useState(() => initialDate.format('YYYY-MM-DD'));

  const months = useMemo(() => {
    let months = [];

    // 1 month from the past
    let currentDate = initialDate.subtract(1, "month");
    // 3 months in the future
    let endDate = initialDate.add(4, "month");

    while (currentDate.isBefore(endDate, "month")) {
      months.push({
        label: currentDate.format("MMMM YYYY"),
        value: currentDate.format("YYYY-MM-DD"),
      });
      currentDate = currentDate.add(1, "month");
    }

    return months;
  }, [initialDate]);

  useEffect(() => {
    form.setFieldsValue({month_id: selectedMonthId});
  }, [form, selectedMonthId]);

  useEffect(() => {
    if (!filterOn)
      setShowRooms([]);
    else
      setShowRooms(defaultCheckedOptions);
  }, [filterOn, defaultCheckedOptions, setShowRooms]);

  useEffect(() => {
    setShowRooms(checkedList);
  }, [checkedList, setShowRooms]);

  useEffect(() => {
    setCheckedList(defaultCheckedOptions);
  }, [defaultCheckedOptions]);

  const checkboxOptions = useMemo(() => {
    return filteredOptions.map((option) => {
      const isDepartmentOption =
        option === OVERVIEW_FILTER_ROOM_TYPES.DEPARTMENT_ROOM;
      const disableDepartmentOption =
        isDepartmentOption && !!user && !hasDepartmentRooms;

      if (!disableDepartmentOption) {
        return {label: t(option), value: option};
      }

      return {
        label: (
          <Tooltip title={t('Nie sú katedrové miestnosti')}>
            <span>{t(option)}</span>
          </Tooltip>
        ),
        value: option,
        disabled: true,
      };
    });
  }, [filteredOptions, hasDepartmentRooms, t, user]);

  return (
    <Form
      form={form}
      initialValues={{month_id: initialDate.format("YYYY-MM-DD")}}
      layout={'incline'}
    >
      <Row gutter={16}>
        <Col>
          <FormSelect
            label={t('Mesiac')}
            name={'month_id'}
            options={months}
            rules={[required(t)]}
            allowClear={false}
            onChange={value => {
              if (!value || value === selectedMonthId) {
                return;
              }

              setSelectedMonthId(value);
              onFinish(dayjs(value));
            }}
            style={{width: 200}}
          />
        </Col>

        <Col>
          <FormItem style={{margin: '5px 0 0 0'}}>
            <p><strong>{t('Filtrovanie miestností')}:</strong></p>
          </FormItem>
        </Col>

        <Col>
          <FormSwitch
            name={'filterOn'}
            checkedChildren={t('Všetky')}
            unCheckedChildren={t('Žiadne')}
            defaultChecked
            onChange={(checked) => {
              setFilterOn(checked);
              if (checked) {
                setCheckedList(filteredOptions);
              } else {
                setCheckedList([]);
              }
            }}
          />
        </Col>

        <Col>
          <FormItem>
            <CheckboxGroup
              options={checkboxOptions}
              value={checkedList}
              onChange={(list) => {
                setCheckedList(list);
              }}
            />
          </FormItem>
        </Col>
      </Row>
    </Form>
  );
};

ReservationsOverviewForm.propTypes = {
  onFinish: PropTypes.func,
  setShowRooms: PropTypes.func,
  hasDepartmentRooms: PropTypes.bool,
};

export default ReservationsOverviewForm;
